import { CSSProperties, useEffect, useState } from "react";
import { api } from "../api.js";
import { ContactButtons } from "../components/ContactButtons.js";
import { useIsModern } from "../theme.js";
import { ClientsTabModern, ClientDetailModern } from "./Clients.modern.js";

interface ClientRow {
  id: string;
  firstName: string;
  lastName: string | null;
  phone: string | null;
  instagram: string | null;
  segment: string | null;
  blacklisted: boolean;
}

const PAGE_SIZES = [20, 50, 100];

const SERVICE_LABEL: Record<string, string> = {
  manicure: "Манікюр",
  pedicure: "Педикюр",
  manicure_pedicure: "Манік + педик",
  other: "Інше",
};

const STATUS_LABEL: Record<string, string> = {
  completed: "✅",
  scheduled: "🕒",
  cancelled: "❌",
  no_show: "🚫",
};

type ClientsView = "all" | "appointments";

export function ClientsTab() {
  const modern = useIsModern();
  if (modern) return <ClientsTabModern />;
  return <LegacyClientsTab />;
}

function LegacyClientsTab() {
  const [view, setView] = useState<ClientsView>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Keep the list mounted while a client profile is open — scroll, search
  // query, current page all survive a back-navigation.
  return (
    <>
      {selectedId && (
        <ClientDetail id={selectedId} onBack={() => setSelectedId(null)} />
      )}
      <div style={{ display: selectedId ? "none" : "block" }}>
        <div className="tabs" style={{ marginBottom: 12 }}>
          <button
            className={view === "all" ? "active" : ""}
            onClick={() => setView("all")}
          >
            Всі клієнти
          </button>
          <button
            className={view === "appointments" ? "active" : ""}
            onClick={() => setView("appointments")}
          >
            Записи
          </button>
        </div>
        {view === "all" ? (
          <AllClients onOpen={setSelectedId} />
        ) : (
          <AppointmentsView onOpen={setSelectedId} />
        )}
      </div>
    </>
  );
}

const SEGMENT_LABEL: Record<string, string> = {
  __ignored: "🔕 Ігноровані",
  new: "🆕 Новачки",
  returning: "🔁 Повторні",
  regular: "💎 Постійні",
  hatiko: "🐕 Хатіко (вірні)",
  loitering: "🌀 Блукаючі",
  ghost: "👻 Не прийшли",
  lead: "📓 Просто в базі",
  lost_1m: "⏳ Втрачені (1 міс)",
  lost_3m: "⏳ Втрачені (3 міс)",
  lost_6m: "⏳ Втрачені (6 міс)",
  lost_1y: "⏳ Втрачені (1+ рік)",
};

function AllClients({ onOpen }: { onOpen: (id: string) => void }) {
  const [q, setQ] = useState("");
  const [segment, setSegment] = useState<string>("");
  const [referralSource, setReferralSource] = useState<string>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState<number | null>(null);
  const [items, setItems] = useState<ClientRow[] | null>(null);
  const [segmentCounts, setSegmentCounts] = useState<Record<string, number>>({});
  const [referralCounts, setReferralCounts] = useState<Record<string, number>>(
    {},
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [q, segment, referralSource]);

  useEffect(() => {
    const t = setTimeout(() => {
      api
        .clients({
          q: q.trim() || undefined,
          page,
          pageSize,
          segment: segment || undefined,
          referralSource: referralSource || undefined,
        })
        .then((r) => {
          setTotal(r.total);
          setItems(r.items as ClientRow[]);
          setSegmentCounts(r.segments ?? {});
          setReferralCounts((r as any).referralSources ?? {});
        })
        .catch((e) => setError(String(e)));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, page, pageSize, segment, referralSource]);

  if (error) {
    return (
      <div className="card">
        <p>Не вдалось завантажити клієнтів.</p>
        <p className="hint">{error}</p>
      </div>
    );
  }

  const lastPage = total ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const from = total ? (page - 1) * pageSize + 1 : 0;
  const to = total ? Math.min(page * pageSize, total) : 0;

  return (
    <div>
      <input
        type="text"
        placeholder="Пошук (імʼя, телефон, інстаграм)"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={searchStyle}
      />

      <select
        value={segment}
        onChange={(e) => setSegment(e.target.value)}
        style={{ ...searchStyle, marginBottom: 8 }}
      >
        <option value="">Всі сегменти</option>
        {Object.entries(SEGMENT_LABEL).map(([key, label]) => {
          const n = segmentCounts[key] ?? 0;
          return (
            <option key={key} value={key}>
              {label} ({n})
            </option>
          );
        })}
      </select>

      <select
        value={referralSource}
        onChange={(e) => setReferralSource(e.target.value)}
        style={{ ...searchStyle, marginBottom: 8 }}
      >
        <option value="">Всі джерела</option>
        {Object.entries(REFERRAL_LABEL_MAP).map(([key, label]) => {
          const n = referralCounts[key] ?? 0;
          return (
            <option key={key} value={key}>
              {label} ({n})
            </option>
          );
        })}
        <option value="__unknown">
          ❓ Не вказано ({referralCounts["__unknown"] ?? 0})
        </option>
      </select>

      <div className="row" style={{ marginBottom: 8 }}>
        <span className="hint">
          {total !== null ? `${from}–${to} з ${total}` : "Завантаження..."}
        </span>
        <span className="spacer" />
        <span className="hint" style={{ marginRight: 6 }}>На сторінці:</span>
        <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}
          style={selectStyle}
        >
          {PAGE_SIZES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {!items ? null : items.length === 0 ? (
        <div className="card">
          <p>Нікого не знайшов</p>
        </div>
      ) : (
        items.map((c) => (
          <button
            key={c.id}
            onClick={() => onOpen(c.id)}
            style={clientCardBtn}
          >
            <div className="row">
              <strong>
                {c.firstName} {c.lastName ?? ""}
              </strong>
              <span className="spacer" />
              <span className="hint">{c.phone ?? ""}</span>
            </div>
            {(c.instagram || c.segment || c.blacklisted) && (
              <div className="hint" style={{ marginTop: 4 }}>
                {c.instagram && <>@{c.instagram} </>}
                {c.segment && <>· {SEGMENT_LABEL[c.segment] ?? c.segment} </>}
                {c.blacklisted && <>· 🚫 ЧС</>}
              </div>
            )}
          </button>
        ))
      )}

      {total !== null && lastPage > 1 && (
        <div className="row" style={{ marginTop: 12 }}>
          <button onClick={() => setPage(1)} disabled={page === 1} style={pageBtn(page === 1)}>
            «
          </button>
          <button onClick={() => setPage(page - 1)} disabled={page === 1} style={pageBtn(page === 1)}>
            ‹
          </button>
          <span className="spacer" />
          <span className="hint">
            Стор. {page} з {lastPage}
          </span>
          <span className="spacer" />
          <button onClick={() => setPage(page + 1)} disabled={page >= lastPage} style={pageBtn(page >= lastPage)}>
            ›
          </button>
          <button onClick={() => setPage(lastPage)} disabled={page >= lastPage} style={pageBtn(page >= lastPage)}>
            »
          </button>
        </div>
      )}
    </div>
  );
}

function AppointmentsView({ onOpen }: { onOpen: (id: string) => void }) {
  const [items, setItems] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .scheduledVisits()
      .then((d) => setItems(d as any[]))
      .catch((e) => setError(String(e)));
  }, []);

  if (error)
    return (
      <div className="card">
        <p>Помилка: {error}</p>
      </div>
    );
  if (!items) return <div className="hint">Завантаження...</div>;
  if (items.length === 0)
    return (
      <div className="card">
        <p style={{ margin: 0 }}>Майбутніх записів нема.</p>
        <p className="hint" style={{ marginTop: 4 }}>
          Записи зʼявляться тут після нічного синку з Bumpix.
        </p>
      </div>
    );

  // Bucket by relative date
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTomorrow = new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000);
  const dayAfterTomorrow = new Date(startOfTomorrow.getTime() + 24 * 60 * 60 * 1000);

  // End of THIS week (Sunday). getDay(): 0=Sunday, 1=Monday, ... 6=Saturday.
  const dow = startOfToday.getDay();
  const daysUntilSundayEnd = (7 - dow) % 7 || 7;
  const endOfThisWeek = new Date(
    startOfToday.getTime() + daysUntilSundayEnd * 24 * 60 * 60 * 1000,
  );

  const today: any[] = [];
  const tomorrow: any[] = [];
  const thisWeek: any[] = [];
  const later: any[] = [];
  for (const v of items) {
    const t = new Date(v.startedAt).getTime();
    if (t < startOfTomorrow.getTime()) today.push(v);
    else if (t < dayAfterTomorrow.getTime()) tomorrow.push(v);
    else if (t < endOfThisWeek.getTime()) thisWeek.push(v);
    else later.push(v);
  }

  return (
    <div>
      <p className="hint" style={{ marginBottom: 8 }}>
        Заплановані візити з Bumpix · всього {items.length}
      </p>
      <Bucket title={`🌅 Сьогодні (${today.length})`} items={today} onOpen={onOpen} defaultOpen alwaysShow />
      <Bucket title={`☀️ Завтра (${tomorrow.length})`} items={tomorrow} onOpen={onOpen} defaultOpen alwaysShow />
      <Bucket title={`📅 Цей тиждень (${thisWeek.length})`} items={thisWeek} onOpen={onOpen} />
      <Bucket title={`📆 Майбутнє (${later.length})`} items={later} onOpen={onOpen} />
    </div>
  );
}

function Bucket({
  title,
  items,
  onOpen,
  defaultOpen = false,
  alwaysShow = false,
}: {
  title: string;
  items: any[];
  onOpen: (id: string) => void;
  defaultOpen?: boolean;
  alwaysShow?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (items.length === 0 && !alwaysShow) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <button onClick={() => setOpen(!open)} style={bucketHeader}>
        {open ? "▾" : "▸"} {title}
      </button>
      {open &&
        (items.length === 0 ? (
          <p className="hint" style={{ marginLeft: 12, marginTop: 4 }}>
            Нікого нема
          </p>
        ) : (
          items.map((v) => <AppointmentRow key={v.id} v={v} onOpen={onOpen} />)
        ))}
    </div>
  );
}

const UA_DAYS = ["нд", "пн", "вт", "ср", "чт", "пт", "сб"];

function pad2(n: number) {
  return n < 10 ? "0" + n : String(n);
}
function fmtDate(d: Date, withWeekday = false): string {
  const dd = pad2(d.getDate());
  const mm = pad2(d.getMonth() + 1);
  return withWeekday ? `${dd}.${mm} ${UA_DAYS[d.getDay()]}` : `${dd}.${mm}`;
}
function fmtTime(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function AppointmentRow({
  v,
  onOpen,
}: {
  v: any;
  onOpen: (id: string) => void;
}) {
  const d = new Date(v.startedAt);
  const isToday = isSameDay(d, new Date());
  const dateLabel = fmtDate(d, !isToday);
  const timeLabel = fmtTime(d);
  return (
    <button
      onClick={() => v.client && onOpen(v.client.id)}
      disabled={!v.client}
      style={appointmentRow}
    >
      <div className="row">
        <span style={{ fontWeight: 700, minWidth: 60 }}>{timeLabel}</span>
        <span className="hint" style={{ marginLeft: 4 }}>{dateLabel}</span>
        <span className="spacer" />
        <span className="hint" style={{ fontSize: 12 }}>
          {SERVICE_LABEL[v.service] ?? v.service}
        </span>
      </div>
      <div className="row" style={{ marginTop: 4 }}>
        <span style={{ fontWeight: 600 }}>
          {v.client
            ? `${v.client.firstName} ${v.client.lastName ?? ""}`.trim()
            : "—"}
        </span>
        <span className="spacer" />
        {v.master && (
          <span className="hint" style={{ fontSize: 12 }}>👩 {v.master.name}</span>
        )}
      </div>
      {v.client && (v.client.instagram || v.client.phone) && (
        <div className="hint" style={{ fontSize: 11, marginTop: 2 }}>
          {v.client.phone && <>{v.client.phone}</>}
          {v.client.phone && v.client.instagram && <> · </>}
          {v.client.instagram && <>@{v.client.instagram}</>}
        </div>
      )}
    </button>
  );
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function ClientDetail(props: { id: string; onBack: () => void }) {
  const modern = useIsModern();
  if (modern) return <ClientDetailModern {...props} />;
  return <LegacyClientDetail {...props} />;
}

function LegacyClientDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [visitsShown, setVisitsShown] = useState(10);

  useEffect(() => {
    setData(null);
    setVisitsShown(10);
    api
      .clientDetail(id)
      .then(setData)
      .catch((e) => setError(String(e)));
  }, [id]);

  if (error)
    return (
      <div>
        <BackBtn onBack={onBack} />
        <div className="card">
          <p>Помилка: {error}</p>
        </div>
      </div>
    );
  if (!data) {
    return (
      <div>
        <BackBtn onBack={onBack} />
        <div className="hint">Завантаження...</div>
      </div>
    );
  }

  const fullName = `${data.firstName} ${data.lastName ?? ""}`.trim();
  const visits = data.visits as any[];
  const completed = visits.filter((v) => v.status === "completed");
  const totalSpent = completed.reduce((s, v) => s + Number(v.price ?? 0), 0);
  const totalTips = completed.reduce((s, v) => s + Number(v.tip ?? 0), 0);

  // Paginate visits newest-first → only group the slice; the rest sits
  // behind "Завантажити ще". Stays grouped by year inside the shown chunk.
  const visitsSorted = [...visits].sort(
    (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
  );
  const visitsSlice = visitsSorted.slice(0, visitsShown);
  const byYear = new Map<number, any[]>();
  for (const v of visitsSlice) {
    const y = new Date(v.startedAt).getFullYear();
    if (!byYear.has(y)) byYear.set(y, []);
    byYear.get(y)!.push(v);
  }

  return (
    <div>
      <BackBtn onBack={onBack} />

      <div className="card">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <h1 style={{ margin: 0, fontSize: 18, flex: 1 }}>{fullName}</h1>
          <button
            onClick={() => setContactsOpen(true)}
            style={{
              padding: "6px 12px",
              background: "var(--button)",
              color: "var(--button-text)",
              border: 0,
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            ✏️ Редагувати
          </button>
        </div>
        <div className="hint">
          {data.phone && <>{data.phone} · </>}
          {data.instagram && <>@{data.instagram} · </>}
          {data.segment && <>{data.segment}</>}
          {data.blacklisted && <> · 🚫 ЧС</>}
        </div>
        <ContactButtons client={data} />
        {data.notes && (
          <div className="hint" style={{ marginTop: 6, fontStyle: "italic" }}>
            📝 {data.notes}
          </div>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 6,
            marginTop: 10,
            fontSize: 12,
          }}
        >
          <Stat label="Візитів" value={completed.length} />
          <Stat label="Витрачено" value={`${formatUah(totalSpent)} грн`} />
          <Stat label="Чайових" value={`${formatUah(totalTips)} грн`} />
        </div>
        {data.birthday && (
          <div className="hint" style={{ marginTop: 6, fontSize: 12 }}>
            🎂 {fmtDate(new Date(data.birthday))}
          </div>
        )}
        <DoNotRemindToggle
          clientId={data.id}
          initial={!!data.doNotRemind}
        />
        <GoogleReviewToggle
          clientId={data.id}
          initial={!!data.googleReviewLeft}
        />
        <BloggerToggle
          clientId={data.id}
          initial={!!data.isBlogger}
        />
      </div>

      {contactsOpen && (
        <ContactsEditor
          clientId={data.id}
          initial={{
            phone: data.phone ?? "",
            instagram: data.instagram ?? "",
            telegramUsername: data.telegramUsername ?? "",
            viberPhone: data.viberPhone ?? "",
          }}
          onClose={() => setContactsOpen(false)}
          onSaved={(v) => {
            data.phone = v.phone;
            data.instagram = v.instagram;
            data.telegramUsername = v.telegramUsername;
            data.viberPhone = v.viberPhone;
            setContactsOpen(false);
          }}
        />
      )}

      <ReferralSourceEditor
        clientId={data.id}
        initial={data.referralSource ?? null}
      />

      <ClientDiscounts clientId={data.id} clientName={`${data.firstName} ${data.lastName ?? ""}`.trim()} />

      <ClientNotes clientId={data.id} initialNotes={data.appNotes ?? []} />

      <h2 style={{ margin: "12px 0 6px" }}>
        Історія візитів
        {visits.length > 0 && (
          <span className="hint" style={{ fontWeight: 400, marginLeft: 6 }}>
            · {Math.min(visitsShown, visits.length)} з {visits.length}
          </span>
        )}
      </h2>

      {visits.length === 0 && (
        <div className="card">
          <p style={{ margin: 0 }}>Ще не була у нас.</p>
        </div>
      )}

      {[...byYear.entries()]
        .sort((a, b) => b[0] - a[0])
        .map(([year, vs]) => (
          <div key={year} style={{ marginBottom: 12 }}>
            <div className="hint" style={{ margin: "4px 0 4px 4px" }}>{year}</div>
            {vs.map((v: any) => (
              <VisitRow key={v.id} v={v} />
            ))}
          </div>
        ))}

      {visitsShown < visits.length && (
        <button
          className="btn-secondary"
          onClick={() => setVisitsShown(visitsShown + 10)}
          style={{
            width: "100%",
            padding: "10px 14px",
            marginTop: 6,
            marginBottom: 12,
            borderRadius: 10,
            border: "1px dashed rgba(127,127,127,0.4)",
            background: "transparent",
            color: "var(--text)",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          ⬇ Завантажити ще (+{Math.min(10, visits.length - visitsShown)})
        </button>
      )}
    </div>
  );
}

function VisitRow({ v }: { v: any }) {
  const d = new Date(v.startedAt);
  const dateLabel = fmtDate(d);
  const timeLabel = fmtTime(d);
  const service = v.serviceLabel ?? SERVICE_LABEL[v.service] ?? v.service;
  const price = Number(v.price ?? 0);
  return (
    <div style={visitRow}>
      <div className="row">
        <span style={{ fontWeight: 600, minWidth: 50 }}>{dateLabel}</span>
        <span className="hint" style={{ marginLeft: 4 }}>{timeLabel}</span>
        <span className="spacer" />
        <span>{STATUS_LABEL[v.status] ?? v.status}</span>
      </div>
      <div className="row" style={{ marginTop: 2 }}>
        <span style={{ fontSize: 13 }}>{service}</span>
        <span className="spacer" />
        {price > 0 && (
          <span style={{ fontWeight: 600, fontSize: 13, marginLeft: 8 }}>
            {formatUah(price)} грн
          </span>
        )}
      </div>
      {v.master && (
        <div className="hint" style={{ fontSize: 11, marginTop: 1 }}>
          👩 {v.master.name}
        </div>
      )}
    </div>
  );
}

function ClientNotes({
  clientId,
  initialNotes,
}: {
  clientId: string;
  initialNotes: { id: string; text: string; createdAt: string }[];
}) {
  const [notes, setNotes] = useState(initialNotes);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  async function add() {
    if (!draft.trim() || saving) return;
    setSaving(true);
    try {
      const n = await api.addClientNote(clientId, draft.trim());
      setNotes([n, ...notes]);
      setDraft("");
    } catch (e) {
      alert(String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>💬 Коментарі / хто є хто</h2>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          type="text"
          placeholder="Напр. 'просила більше не дзвонити по обіді'"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          style={{
            flex: 1,
            padding: 8,
            border: "1px solid rgba(127,127,127,0.3)",
            borderRadius: 6,
            background: "var(--bg)",
            color: "var(--text)",
            fontSize: 13,
          }}
        />
        <button
          onClick={add}
          disabled={saving || !draft.trim()}
          style={{
            padding: "8px 12px",
            background: "var(--button)",
            color: "var(--button-text)",
            border: 0,
            borderRadius: 6,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Додати
        </button>
      </div>
      {notes.length === 0 ? (
        <p className="hint" style={{ marginTop: 8, marginBottom: 0 }}>
          Поки нема коментарів. Вони зʼявляються коли адмін відмічає що
          відповіла клієнтка у нагадуванні.
        </p>
      ) : (
        <div style={{ marginTop: 8 }}>
          {notes.map((n) => (
            <div
              key={n.id}
              style={{
                borderLeft: "3px solid var(--button)",
                paddingLeft: 8,
                marginBottom: 8,
              }}
            >
              <div style={{ fontSize: 13 }}>{n.text}</div>
              <div className="hint" style={{ fontSize: 11 }}>
                {fmtDate(new Date(n.createdAt))}{" "}
                {fmtTime(new Date(n.createdAt))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GoogleReviewToggle({
  clientId,
  initial,
}: {
  clientId: string;
  initial: boolean;
}) {
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  async function toggle() {
    if (saving) return;
    setSaving(true);
    try {
      const r = await api.setClientGoogleReview(clientId, !value);
      setValue(r.googleReviewLeft);
    } catch (e) {
      alert(String(e));
    } finally {
      setSaving(false);
    }
  }
  return (
    <button
      onClick={toggle}
      disabled={saving}
      style={{
        marginTop: 8,
        marginLeft: 6,
        padding: "6px 10px",
        background: value ? "rgba(16,185,129,0.18)" : "var(--secondary-bg)",
        color: value ? "#059669" : "var(--text)",
        border: "1px solid rgba(127,127,127,0.25)",
        borderRadius: 6,
        fontSize: 12,
        cursor: "pointer",
      }}
      title="Позначити, що клієнтка реально лишила Google-відгук (для воронки)"
    >
      {value ? "🌟 Лишила Google-відгук" : "☆ Google-відгук — ні"}
    </button>
  );
}

function BloggerToggle({
  clientId,
  initial,
}: {
  clientId: string;
  initial: boolean;
}) {
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  async function toggle() {
    if (saving) return;
    const next = !value;
    if (
      !window.confirm(
        next
          ? "Позначити клієнтку як блогерку? (без нагадувань про запис/реферала, не рахується як «нова»)"
          : "Прибрати тег «Блогер»?",
      )
    )
      return;
    setSaving(true);
    try {
      const r = await api.setClientBlogger(clientId, next);
      setValue(r.isBlogger);
    } catch (e) {
      alert(String(e));
    } finally {
      setSaving(false);
    }
  }
  return (
    <button
      onClick={toggle}
      disabled={saving}
      style={{
        marginTop: 8,
        marginLeft: 6,
        padding: "6px 10px",
        background: value ? "rgba(236,72,153,0.16)" : "var(--secondary-bg)",
        color: value ? "#be185d" : "var(--text)",
        border: "1px solid rgba(127,127,127,0.25)",
        borderRadius: 6,
        fontSize: 12,
        cursor: "pointer",
      }}
      title="Тег «блогер»: без нагадувань, не рахується як нова клієнтка"
    >
      {value ? "📣 Блогер" : "☆ Блогер — ні"}
    </button>
  );
}

function DoNotRemindToggle({
  clientId,
  initial,
}: {
  clientId: string;
  initial: boolean;
}) {
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  async function toggle() {
    if (saving) return;
    setSaving(true);
    try {
      const r = await api.setClientDoNotRemind(clientId, !value);
      setValue(r.doNotRemind);
    } catch (e) {
      alert(String(e));
    } finally {
      setSaving(false);
    }
  }
  return (
    <button
      onClick={toggle}
      disabled={saving}
      style={{
        marginTop: 8,
        padding: "6px 10px",
        background: value ? "#fde68a" : "var(--secondary-bg)",
        color: value ? "#7c2d12" : "var(--text)",
        border: "1px solid rgba(127,127,127,0.25)",
        borderRadius: 6,
        fontSize: 12,
        cursor: "pointer",
      }}
    >
      {value ? "🔕 Не нагадуємо · вернути" : "🔔 Нагадування йдуть · вимкнути"}
    </button>
  );
}

function ReferralSourceEditor({
  clientId,
  initial,
}: {
  clientId: string;
  initial: string | null;
}) {
  const isKnown = (v: string) => v in REFERRAL_LABEL_MAP;
  const initialIsCustom = !!initial && !isKnown(initial);

  const [select, setSelect] = useState<string>(
    initial ? (initialIsCustom ? "__other" : initial) : "",
  );
  const [customText, setCustomText] = useState<string>(
    initialIsCustom ? initial! : "",
  );
  const [saved, setSaved] = useState<string | null>(initial);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  async function persist(source: string) {
    if (saving) return;
    setSaving(true);
    setJustSaved(false);
    try {
      const r = await api.setClientReferralSource(clientId, source);
      setSaved(r.referralSource);
      setJustSaved(true);
    } catch (e) {
      alert(String(e));
    } finally {
      setSaving(false);
    }
  }

  function onSelectChange(v: string) {
    setSelect(v);
    setJustSaved(false);
    // Picking a concrete option saves immediately; "Інше" reveals the input.
    if (v !== "__other") void persist(v);
  }

  const customDirty =
    select === "__other" && customText.trim() !== (saved ?? "");

  return (
    <div className="card" style={{ marginTop: 8 }}>
      <h2 style={{ margin: "0 0 8px", fontSize: 15 }}>📍 Звідки дізналась</h2>
      <select
        value={select}
        onChange={(e) => onSelectChange(e.target.value)}
        disabled={saving}
        style={referralSelect}
      >
        <option value="">— Не вказано —</option>
        {Object.entries(REFERRAL_LABEL_MAP).map(([key, label]) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
        <option value="__other">✏️ Інше (вписати вручну)</option>
      </select>

      {select === "__other" && (
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <input
            type="text"
            placeholder="Напр. 'подруга Софія', 'візитка', 'банер'"
            value={customText}
            onChange={(e) => {
              setCustomText(e.target.value);
              setJustSaved(false);
            }}
            style={{
              flex: 1,
              padding: 10,
              border: "1px solid rgba(127,127,127,0.3)",
              borderRadius: 8,
              background: "var(--bg)",
              color: "var(--text)",
              fontSize: 14,
              boxSizing: "border-box",
            }}
          />
          <button
            onClick={() => persist(customText.trim())}
            disabled={saving || !customText.trim() || !customDirty}
            style={{
              padding: "8px 14px",
              background: "var(--button)",
              color: "var(--button-text)",
              border: 0,
              borderRadius: 8,
              fontSize: 14,
              cursor: "pointer",
              opacity: saving || !customText.trim() || !customDirty ? 0.5 : 1,
            }}
          >
            Зберегти
          </button>
        </div>
      )}

      <div className="hint" style={{ marginTop: 8, fontSize: 12 }}>
        {saving
          ? "Зберігаю..."
          : justSaved
            ? "✅ Збережено"
            : saved
              ? `Зараз: ${referralLabel(saved)}`
              : "Поки не вказано"}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="hint" style={{ fontSize: 11 }}>{label}</div>
      <div style={{ fontWeight: 600 }}>{value}</div>
    </div>
  );
}

function BackBtn({ onBack }: { onBack: () => void }) {
  return (
    <button onClick={onBack} style={backBtn}>
      ← Назад
    </button>
  );
}

function formatUah(n: number): string {
  return Math.round(n).toLocaleString("uk-UA");
}

const REFERRAL_LABEL_MAP: Record<string, string> = {
  instagram: "📷 Instagram",
  google_maps: "🗺️ Google Maps",
  tiktok: "🎵 TikTok",
  public: "📢 Паблік",
  targeted_ads: "🎯 Таргет",
  recommendation: "🤝 Порекомендували",
  business_card: "💳 Візитка",
  cross_marketing: "🔁 Крос-маркетинг",
};
function referralLabel(src: string): string {
  return REFERRAL_LABEL_MAP[src] ?? src;
}

const searchStyle: CSSProperties = {
  display: "block",
  width: "100%",
  padding: 10,
  marginBottom: 8,
  border: "1px solid rgba(127,127,127,0.3)",
  borderRadius: 8,
  background: "var(--bg)",
  color: "var(--text)",
  fontSize: 14,
  boxSizing: "border-box",
};

const selectStyle: CSSProperties = {
  padding: "4px 8px",
  border: "1px solid rgba(127,127,127,0.3)",
  borderRadius: 6,
  background: "var(--bg)",
  color: "var(--text)",
  fontSize: 13,
};

const referralSelect: CSSProperties = {
  display: "block",
  width: "100%",
  padding: 10,
  border: "1px solid rgba(127,127,127,0.3)",
  borderRadius: 8,
  background: "var(--bg)",
  color: "var(--text)",
  fontSize: 15,
  boxSizing: "border-box",
};

const clientCardBtn: CSSProperties = {
  display: "block",
  width: "100%",
  textAlign: "left",
  background: "var(--secondary-bg)",
  color: "var(--text)",
  border: 0,
  borderRadius: 12,
  padding: 12,
  marginBottom: 8,
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: 14,
};

const bucketHeader: CSSProperties = {
  width: "100%",
  textAlign: "left",
  background: "var(--secondary-bg)",
  color: "var(--text)",
  border: 0,
  borderRadius: 8,
  padding: "10px 12px",
  fontWeight: 600,
  fontSize: 15,
  cursor: "pointer",
  marginBottom: 6,
};

const appointmentRow: CSSProperties = {
  display: "block",
  width: "100%",
  textAlign: "left",
  background: "var(--bg)",
  color: "var(--text)",
  border: "1px solid rgba(127,127,127,0.2)",
  borderRadius: 8,
  padding: 10,
  marginBottom: 6,
  cursor: "pointer",
  fontFamily: "inherit",
};

const visitRow: CSSProperties = {
  background: "var(--secondary-bg)",
  borderRadius: 8,
  padding: 8,
  marginBottom: 4,
};

const backBtn: CSSProperties = {
  background: "transparent",
  border: 0,
  color: "var(--link)",
  fontSize: 14,
  padding: "4px 0",
  marginBottom: 4,
  cursor: "pointer",
};

function pageBtn(disabled: boolean): CSSProperties {
  return {
    padding: "6px 12px",
    background: "var(--secondary-bg)",
    color: "var(--text)",
    border: "1px solid rgba(127,127,127,0.25)",
    borderRadius: 6,
    fontSize: 14,
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.4 : 1,
  };
}

// ---- Discount tracker (one-time discount promised to a client) ----

interface ClientDiscount {
  id: string;
  text: string;
  status: "active" | "applied" | "cancelled";
  createdAt: string;
  appliedAt: string | null;
  cancelledAt: string | null;
}

function ClientDiscounts({
  clientId,
  clientName,
}: {
  clientId: string;
  clientName: string;
}) {
  const [items, setItems] = useState<ClientDiscount[] | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  function load() {
    api
      .listClientDiscounts(clientId)
      .then((d) => setItems(d as ClientDiscount[]))
      .catch((e) => setError(String(e)));
  }
  useEffect(load, [clientId]);

  async function create() {
    if (!draft.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.createDiscount(clientId, draft.trim());
      setDraft("");
      setShowForm(false);
      load();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function applied(id: string) {
    try {
      await api.markDiscountApplied(id);
      load();
    } catch (e) {
      setError(String(e));
    }
  }
  async function cancel(id: string) {
    if (!confirm("Скасувати знижку?")) return;
    try {
      await api.cancelDiscount(id);
      load();
    } catch (e) {
      setError(String(e));
    }
  }

  if (!items) return null;
  const active = items.filter((d) => d.status === "active");
  const history = items.filter((d) => d.status !== "active");

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
        <strong style={{ fontSize: 16 }}>💸 Знижки</strong>
        <span style={{ flex: 1 }} />
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            style={{
              background: "transparent",
              border: "1px dashed rgba(127,127,127,0.4)",
              borderRadius: 8,
              padding: "4px 10px",
              fontSize: 13,
              fontWeight: 600,
              color: "var(--text)",
              cursor: "pointer",
            }}
          >
            ➕ Додати
          </button>
        )}
      </div>

      {showForm && (
        <div style={{ marginBottom: 10 }}>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="напр. -20% за відгук / -100 грн / безкоштовний дизайн"
            style={{
              width: "100%",
              padding: "10px 12px",
              border: "1px solid rgba(127,127,127,0.3)",
              borderRadius: 8,
              background: "var(--bg)",
              color: "var(--text)",
              fontSize: 14,
              boxSizing: "border-box",
            }}
            autoFocus
            maxLength={500}
          />
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            <button
              onClick={create}
              disabled={busy || !draft.trim()}
              style={{
                flex: 1,
                padding: "8px 14px",
                background: !draft.trim() ? "rgba(127,127,127,0.2)" : "#10b981",
                color: !draft.trim() ? "var(--hint)" : "#fff",
                border: 0,
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 700,
                cursor: !draft.trim() ? "not-allowed" : "pointer",
              }}
            >
              {busy ? "..." : "💾 Зберегти"}
            </button>
            <button
              onClick={() => { setShowForm(false); setDraft(""); }}
              style={{
                padding: "8px 14px",
                background: "transparent",
                color: "var(--hint)",
                border: 0,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {active.length === 0 && !showForm && (
        <p className="hint" style={{ margin: 0, fontSize: 13 }}>
          Активних знижок нема. Як домовишся з {clientName} — додай тут, бот нагадає коли вона запишеться.
        </p>
      )}

      {active.map((d) => (
        <div
          key={d.id}
          style={{
            background: "rgba(16,185,129,0.08)",
            border: "1px solid rgba(16,185,129,0.3)",
            borderRadius: 8,
            padding: "10px 12px",
            marginBottom: 6,
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
            🎁 {d.text}
          </div>
          <div className="hint" style={{ fontSize: 11, marginBottom: 8 }}>
            з {new Date(d.createdAt).toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit" })}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={() => applied(d.id)}
              style={{
                padding: "4px 10px",
                background: "rgba(16,185,129,0.15)",
                color: "#047857",
                border: "1px solid rgba(16,185,129,0.3)",
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              ✓ Застосовано
            </button>
            <button
              onClick={() => cancel(d.id)}
              style={{
                padding: "4px 10px",
                background: "transparent",
                color: "#6b7280",
                border: "1px solid rgba(127,127,127,0.3)",
                borderRadius: 6,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              🗑 Скасувати
            </button>
          </div>
        </div>
      ))}

      {history.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary
            className="hint"
            style={{ fontSize: 12, cursor: "pointer" }}
          >
            Історія знижок · {history.length}
          </summary>
          <div style={{ marginTop: 6 }}>
            {history.map((d) => (
              <div
                key={d.id}
                style={{ fontSize: 12, padding: "4px 0", opacity: 0.7 }}
              >
                {d.status === "applied" ? "✓" : "✕"} {d.text} ·{" "}
                {new Date(d.createdAt).toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit" })}
              </div>
            ))}
          </div>
        </details>
      )}

      {error && (
        <p className="hint" style={{ fontSize: 12, color: "#dc2626" }}>{error}</p>
      )}
    </div>
  );
}

// ---- Client contacts editor: phone / instagram / telegram / viber ----

function ContactsEditor({
  clientId,
  initial,
  onClose,
  onSaved,
}: {
  clientId: string;
  initial: {
    phone: string;
    instagram: string;
    telegramUsername: string;
    viberPhone: string;
  };
  onClose: () => void;
  onSaved: (v: {
    phone: string | null;
    instagram: string | null;
    telegramUsername: string | null;
    viberPhone: string | null;
  }) => void;
}) {
  const [phone, setPhone] = useState(initial.phone);
  const [instagram, setInstagram] = useState(initial.instagram);
  const [telegram, setTelegram] = useState(initial.telegramUsername);
  const [viber, setViber] = useState(initial.viberPhone);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    phone.trim() !== initial.phone.trim() ||
    instagram.trim().replace(/^@/, "") !== initial.instagram.trim().replace(/^@/, "") ||
    telegram.trim().replace(/^@/, "") !== initial.telegramUsername.trim().replace(/^@/, "") ||
    viber.trim() !== initial.viberPhone.trim();

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.updateClientContacts(clientId, {
        phone: phone.trim(),
        instagram: instagram.trim(),
        telegramUsername: telegram.trim(),
        viberPhone: viber.trim(),
      });
      onSaved(r);
      setPhone(r.phone ?? "");
      setInstagram(r.instagram ?? "");
      setTelegram(r.telegramUsername ?? "");
      setViber(r.viberPhone ?? "");
    } catch (e) {
      setError(String(e).replace(/^Error:\s*\d+:\s*/, ""));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginTop: 10 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
        <strong style={{ fontSize: 15 }}>✏️ Редагувати контакти</strong>
        <span style={{ flex: 1 }} />
        <button
          onClick={() => {
            setPhone(initial.phone);
            setInstagram(initial.instagram);
            setTelegram(initial.telegramUsername);
            setViber(initial.viberPhone);
            setError(null);
            onClose();
          }}
          style={{
            background: "transparent",
            border: 0,
            fontSize: 18,
            cursor: "pointer",
            color: "var(--hint)",
          }}
        >
          ✕
        </button>
      </div>

      <FieldRow label="📞 Телефон" value={phone} onChange={setPhone} placeholder="+380501234567" />
      <FieldRow
        label="📷 Instagram"
        value={instagram}
        onChange={setInstagram}
        placeholder="kateryna_n (без @)"
      />
      <FieldRow
        label="✈️ Telegram"
        value={telegram}
        onChange={setTelegram}
        placeholder="kateryna_n (без @)"
      />
      <FieldRow
        label="💜 Viber"
        value={viber}
        onChange={setViber}
        placeholder={
          phone ? `${phone} (використовується якщо пусте)` : "+380501234567"
        }
        hint="Залиш пустим — Viber відкриється за номером телефону. Заповни тільки якщо у Viber інший номер."
      />

      {error && (
        <p className="hint" style={{ fontSize: 12, color: "#dc2626", marginTop: 6 }}>
          {error}
        </p>
      )}

      <button
        onClick={save}
        disabled={busy || !dirty}
        style={{
          marginTop: 10,
          width: "100%",
          padding: "10px 14px",
          background: !dirty ? "rgba(127,127,127,0.2)" : "var(--button)",
          color: !dirty ? "var(--hint)" : "var(--button-text)",
          border: 0,
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 700,
          cursor: !dirty ? "not-allowed" : "pointer",
        }}
      >
        {busy ? "..." : dirty ? "💾 Зберегти" : "Без змін"}
      </button>
    </div>
  );
}

function FieldRow({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
}) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div className="hint" style={{ fontSize: 11, marginBottom: 3 }}>
        {label}
      </div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: "100%",
          padding: "8px 10px",
          border: "1px solid rgba(127,127,127,0.3)",
          borderRadius: 8,
          background: "var(--bg)",
          color: "var(--text)",
          fontSize: 14,
          boxSizing: "border-box",
        }}
      />
      {hint && (
        <div className="hint" style={{ fontSize: 11, marginTop: 3 }}>
          {hint}
        </div>
      )}
    </div>
  );
}
